import { useCallback, useEffect, useRef, useState } from 'react';

// 조합 창 캡처(PNG Blob)를 Worker에서 인식한다. Worker는 처음 인식할 때 만들어 계속 쓴다.
export default function useCompositionRecognizer() {
  const workerRef = useRef(null);
  const pendingRef = useRef(null); // { id, resolve, reject }
  const nextIdRef = useRef(1);
  const [status, setStatus] = useState(null); // 진행 문구
  const [progress, setProgress] = useState(null); // { done, total }

  const ensureWorker = useCallback(() => {
    if (!workerRef.current) {
      const worker = new Worker(new URL('./recognizer.worker.js', import.meta.url));
      worker.onmessage = ({ data }) => {
        const pending = pendingRef.current;
        if (!pending || data.id !== pending.id) {
          return;
        }
        if (data.type === 'status') {
          setStatus(data.status);
        } else if (data.type === 'progress') {
          setProgress({ done: data.done, total: data.total });
        } else if (data.type === 'result') {
          pendingRef.current = null;
          setStatus(null);
          setProgress(null);
          pending.resolve(data.teams);
        } else if (data.type === 'error') {
          pendingRef.current = null;
          setStatus(null);
          setProgress(null);
          pending.reject(new Error(data.message));
        }
      };
      workerRef.current = worker;
    }
    return workerRef.current;
  }, []);

  const recognize = useCallback(async (blob) => {
    if (pendingRef.current) {
      throw new Error('이미 인식 중이에요.');
    }
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(bitmap, 0, 0);
    const { data, width, height } = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    bitmap.close();

    const id = nextIdRef.current;
    nextIdRef.current += 1;
    const worker = ensureWorker();
    return new Promise((resolve, reject) => {
      pendingRef.current = { id, resolve, reject };
      setStatus('준비 중');
      worker.postMessage({ type: 'recognize', id, width, height, buffer: data.buffer }, [data.buffer]);
    });
  }, [ensureWorker]);

  useEffect(() => () => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
  }, []);

  return { recognize, status, progress, recognizing: status !== null };
}
